import { FC, useEffect } from 'react';

import ComponentError from '@eneco/components/ui/ComponentError/ComponentError';
import { useCustomerGetCustomerProfile } from '@eneco/dc/hooks';
import useDC from '@eneco/dc/useDC';
import { typedYupResolver } from '@eneco/hooks/form';
import { I18nProvider, useTranslation } from '@eneco/i18n';
import { Button, Form, Heading, InputText, Skeleton, Stack, Text } from '@eneco/sparky';
import { useTracking } from '@eneco/tracking';
import { Customers_Profile_CustomerProfileResponse, DC_Domain_Models_Products_ProductType } from '@monorepo-types/dc';
import { useForm } from 'react-hook-form';

import { FieldErrorNotification } from './components/FieldErrorNotification/FieldErrorNotification';
import { MeterCard } from './components/MeterCard/MeterCard';
import { SaveReadingNotification } from './components/SaveReadingNotification/SaveReadingNotification';
import { FormDeviationReason, FormValues, validationSchema } from './ConfirmationStep.schema';
import { useMeterReadingsFlow } from '../state/MeterReadingsFlow.machine';
import { ReadingMeterModel, SEND_EVENT } from '../state/MeterReadingsFlow.types';
import { Steps } from '../tracking';

export const ConfirmationStep: FC = () => {
  const { label } = useDC();

  return (
    <I18nProvider dictionary={locale => import(`./content/ConfirmationStep.${label}.${locale}.json`)}>
      <ConfirmationStepLoading />
    </I18nProvider>
  );
};

const ConfirmationStepLoading: FC = () => {
  const { isLoading: isTranslationLoading } = useTranslation();
  const { state } = useMeterReadingsFlow();
  const shouldFetchProfile = state.context.isAuthenticated;
  const { data, error, isValidating } = useCustomerGetCustomerProfile(shouldFetchProfile ? {} : null);

  if (isValidating || isTranslationLoading) {
    return <Skeleton height={350} />;
  }

  if (shouldFetchProfile && (!data || error)) {
    return <ComponentError />;
  }

  return <ConfirmationStepComponent customers={data} />;
};

export const ConfirmationStepComponent: FC<{ customers?: Customers_Profile_CustomerProfileResponse }> = ({
  customers,
}) => {
  const { trackFunnelStep } = useTracking();
  const { t } = useTranslation();
  const { state, send } = useMeterReadingsFlow();

  useEffect(() => {
    trackFunnelStep({
      step: Steps.CONFIRMATION,
      funnel: state.context.funnelName,
    });
  }, [state.context.funnelName, trackFunnelStep]);

  const customerEmail = customers?.contact?.emailAddress;
  const showEmailCorrespondenceField = state.context.isAuthenticated && !customerEmail;

  const meters = Object.entries(state.context.meters || {}) as [
    DC_Domain_Models_Products_ProductType,
    ReadingMeterModel,
  ][];

  const defaultMeterDeviationValues = meters.reduce<Partial<FormValues['meters']>>(
    (values, [name, { deviationReasonCode, deviationReasonExplanation }]) => ({
      ...values,
      [name]: {
        deviationReasonCode,
        deviationReasonExplanation,
      },
    }),
    {} as FormValues['meters'],
  );

  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
  } = useForm<FormValues>({
    defaultValues: {
      meters: defaultMeterDeviationValues,
      email: '',
    },
    resolver: typedYupResolver<FormValues>(validationSchema(showEmailCorrespondenceField)),
  });

  const onSubmit = ({ meters, email }: FormValues) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const payload: any = {
      type: 'SEND',
      meters: (Object.entries(meters) as [keyof FormValues['meters'], FormDeviationReason][]).reduce<
        SEND_EVENT['meters']
      >(
        (meters, [name, { deviationReasonCode, deviationReasonExplanation }]) => ({
          ...meters,
          [name]: {
            deviationReasonCode: deviationReasonCode || undefined,
            deviationReasonExplanation,
          },
        }),
        {},
      ),
      email,
      phoneNumber: customers?.contact?.phoneNumber,
      mobilePhoneNumber: customers?.contact?.mobilePhoneNumber,
    };
    send(payload);
  };

  const isLoading = state.matches({ CONFIRMATION: 'LOADING' });

  return (
    <Stack gap="6">
      <Heading as="h1" size="M">
        {t('title')}
      </Heading>

      <Form onSubmit={handleSubmit(onSubmit)}>
        <Stack gap="10">
          <Stack gap="6">
            {meters.map(([name, meter]) => (
              <MeterCard key={name} name={name} meter={meter} control={control} />
            ))}
          </Stack>

          {showEmailCorrespondenceField && (
            <Stack gap="6">
              <Stack gap="2">
                <Heading size="XS" as="h4">
                  {t('emailCorrespondenceTitle')}
                </Heading>
                <Text>{t('emailCorrespondenceText')}</Text>
              </Stack>
              <InputText
                {...register('email')}
                label={t('emailCorrespondenceFormField.label')}
                placeholder={t('emailCorrespondenceFormField.placeholder')}
                error={errors.email?.type && t(`emailCorrespondenceFormField.${errors.email.message}Message`)}
              />
            </Stack>
          )}

          <FieldErrorNotification control={control} />
          <SaveReadingNotification />

          <Stack.Item>
            <Button type="submit" isLoading={isLoading}>
              {t('confirmButtonLabel')}
            </Button>
          </Stack.Item>
        </Stack>
      </Form>
    </Stack>
  );
};
